import * as React from 'react';
import {
  MenuTrigger,
  Menu,
  MenuItem,
  Popover,
  Button as RACButton,
  type MenuItemProps,
} from 'react-aria-components';
import { DownSmallLine } from '@mingcute/react';

import { cn } from '../lib/cn';

import { Button, type ButtonProps } from './button';

/**
 * Klaret SplitButton (handoff §10.1).
 * Primary segment + 28 px chevron segment divided by a 1 px on-accent hairline at 24% opacity.
 * Dropdown via RAC MenuTrigger/Menu/MenuItem — no Radix.
 */
export interface SplitButtonItem {
  key: string;
  label: React.ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  danger?: boolean;
}

export interface SplitButtonProps extends Omit<ButtonProps, 'rightIcon'> {
  items: SplitButtonItem[];
  menuLabel?: string;
}

export const SplitButton = React.forwardRef<HTMLButtonElement, SplitButtonProps>(
  (
    {
      items,
      menuLabel = 'Open menu',
      children,
      variant = 'primary',
      size = 'md',
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <span className={cn('inline-flex', className)}>
        {/* Primary action segment */}
        <Button
          ref={ref}
          variant={variant}
          size={size}
          className="rounded-r-none"
          {...props}
        >
          {children}
        </Button>

        {/* Chevron / dropdown segment */}
        <MenuTrigger>
          <RACButton
            aria-label={menuLabel}
            className={cn(
              // match Button primary styles manually — variant token passed via data
              'inline-flex items-center justify-center rounded-l-none rounded-r-[var(--tvl-r2)]',
              'border-l border-[var(--tvl-on-accent)]/25',
              'transition-[background-color] duration-[var(--tvl-d-fast)] ease-[var(--tvl-ease-out)]',
              'outline-none',
              'focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
              // sizes
              size === 'sm' && 'h-7 w-7 text-[13px]',
              size === 'md' && 'h-9 w-9',
              size === 'lg' && 'h-11 w-9',
              // variant colours — mirror Button
              variant === 'primary' &&
                'bg-[var(--tvl-accent)] text-[var(--tvl-on-accent)] hover:bg-[var(--tvl-accent-hover)] pressed:bg-[var(--tvl-accent-pressed)]',
              variant === 'secondary' &&
                'border border-[var(--tvl-border-input)] bg-transparent text-[var(--tvl-ink)] hover:bg-[var(--tvl-wash-hover)] pressed:bg-[var(--tvl-wash-pressed)]',
              variant === 'ghost' &&
                'bg-transparent text-[var(--tvl-ink)] hover:bg-[var(--tvl-wash-hover)] pressed:bg-[var(--tvl-wash-pressed)]',
            )}
          >
            <DownSmallLine size={16} color="currentColor" aria-hidden />
          </RACButton>
          <Popover
            placement="bottom end"
            offset={4}
            className={cn(
              'z-[var(--tvl-z-popover)] min-w-[10rem] overflow-hidden',
              'rounded-[var(--tvl-r3)] border border-[var(--tvl-hairline)]',
              'bg-[var(--tvl-raised)] p-1 shadow-[var(--tvl-e2)]',
              // enter/exit
              'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
              'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95',
              'placement-bottom:slide-in-from-top-1',
              'placement-top:slide-in-from-bottom-1',
            )}
          >
            <Menu
              className="outline-none"
              onAction={(key) => {
                const item = items.find((i) => i.key === String(key));
                item?.onSelect?.();
              }}
            >
              {items.map((item) => (
                <MenuItem
                  key={item.key}
                  id={item.key}
                  isDisabled={item.disabled}
                  className={cn(
                    'flex cursor-pointer select-none items-center rounded-[var(--tvl-r2)] px-2 py-1.5',
                    'text-[14px] leading-[20px] outline-none',
                    'text-[var(--tvl-ink)]',
                    'hover:bg-[var(--tvl-wash-hover)] focus:bg-[var(--tvl-wash-hover)]',
                    'pressed:bg-[var(--tvl-wash-pressed)]',
                    'disabled:pointer-events-none disabled:text-[var(--tvl-ink-disabled)]',
                    item.danger && 'text-[var(--tvl-danger)]',
                  )}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Popover>
        </MenuTrigger>
      </span>
    );
  },
);
SplitButton.displayName = 'SplitButton';
